import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Popover, Typography } from 'antd';

/**
 * TCR-2458 — cột Mã ERP / Sản phẩm ở danh sách đơn (Ecom + POS) chỉ hiện vài dòng đầu
 * kèm "+N … khác". Dòng "+N" bấm được, mở popup liệt kê ĐỦ mọi mã/sản phẩm của đơn
 * (kể cả các dòng đang hiện) để đối chiếu mà không phải mở chi tiết đơn.
 */

const { Text } = Typography;

const MORE_LINK_COLOR = '#0958d9';
/** Giữ màu xám của chữ "+N … khác" như trước — chỉ thêm khả năng bấm (QA TCR-2458). */
const MORE_TRIGGER_COLOR = '#6b6b6b';
const POPUP_MAX_HEIGHT = 320;
const IMAGE_SIZE = 32;

/**
 * Popover nằm trong portal nhưng sự kiện React vẫn nổi qua cây component về dòng bảng
 * ⇒ chặn ở cả nút mở lẫn nội dung popup, tránh mở chi tiết/chọn dòng ngoài ý muốn.
 */
const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

interface MoreItemsPopoverProps {
  rest: number;
  unit: string;
  title: string;
  /** Hàm nhận `close` khi một mục trong popup mở màn khác (TCR-2459: popup tồn kho). */
  children: ReactNode | ((close: () => void) => ReactNode);
  width?: number;
}

function MoreItemsPopover({ rest, unit, title, children, width = 280 }: MoreItemsPopoverProps) {
  const [open, setOpen] = useState(false);
  if (rest <= 0) return null;
  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      title={title}
      content={(
        <div
          data-testid="more-items-popover-content"
          onClick={stopPropagation}
          style={{ width, maxHeight: POPUP_MAX_HEIGHT, overflowY: 'auto' }}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    >
      <button
        type="button"
        onClick={stopPropagation}
        style={{
          border: 0,
          background: 'transparent',
          padding: 0,
          color: MORE_TRIGGER_COLOR,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        +{rest} {unit} khác
      </button>
    </Popover>
  );
}

interface ErpSkuLinkProps {
  sku: string;
  onClick: (sku: string) => void;
}

/** Mã ERP bấm được — giữ nguyên màu xanh như chữ cũ, không mở chi tiết/chọn dòng (TCR-2459). */
export function ErpSkuLink({ sku, onClick }: ErpSkuLinkProps) {
  return (
    <button
      type="button"
      title="Xem tồn kho"
      onClick={(event) => {
        event.stopPropagation();
        onClick(sku);
      }}
      style={{
        border: 0,
        background: 'transparent',
        padding: 0,
        color: MORE_LINK_COLOR,
        cursor: 'pointer',
        font: 'inherit',
        textAlign: 'left',
        wordBreak: 'break-all',
      }}
    >
      {sku}
    </button>
  );
}

interface ErpSkuMoreLinkProps {
  /** Toàn bộ mã ERP (đã khử trùng) của đơn. */
  skus: string[];
  previewLimit: number;
  /** TCR-2459 — bấm vào một mã trong popup (Đơn Ecom mở popup tồn kho của mã đó). */
  onSkuClick?: (sku: string) => void;
}

export function ErpSkuMoreLink({ skus, previewLimit, onSkuClick }: ErpSkuMoreLinkProps) {
  return (
    <MoreItemsPopover rest={skus.length - previewLimit} unit="mã ERP" title={`Mã ERP (${skus.length})`}>
      {(close) => skus.map((sku) => (
        <div key={sku} style={{ padding: '2px 0' }}>
          <Text data-testid="more-items-erp-sku" copyable={{ text: sku }} style={{ color: MORE_LINK_COLOR }}>
            {onSkuClick ? <ErpSkuLink sku={sku} onClick={(value) => { close(); onSkuClick(value); }} /> : sku}
          </Text>
        </div>
      ))}
    </MoreItemsPopover>
  );
}

export interface MoreProductItem {
  key: string;
  name: string;
  sku?: string;
  quantity?: number;
  imageUrl?: string;
}

interface ProductMoreLinkProps {
  items: MoreProductItem[];
  previewLimit: number;
}

export function ProductMoreLink({ items, previewLimit }: ProductMoreLinkProps) {
  return (
    <MoreItemsPopover rest={items.length - previewLimit} unit="sản phẩm" title={`Sản phẩm (${items.length})`} width={360}>
      {items.map((item) => (
        <div
          key={item.key}
          data-testid="more-items-product"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}
        >
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.name}
              style={{ width: IMAGE_SIZE, height: IMAGE_SIZE, borderRadius: 6, objectFit: 'cover', background: '#f5f5f5', flex: 'none' }}
            />
          ) : (
            <span
              aria-hidden="true"
              style={{ width: IMAGE_SIZE, height: IMAGE_SIZE, borderRadius: 6, background: '#f0f0f0', border: '1px solid #e5e7eb', flex: 'none' }}
            />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ lineHeight: 1.35, wordBreak: 'break-word' }}>{item.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {[item.sku, item.quantity != null ? `SL: ${item.quantity}` : null].filter(Boolean).join(' · ')}
            </Text>
          </div>
        </div>
      ))}
    </MoreItemsPopover>
  );
}
